import { Checkbox, DEFAULT_TIMEZONE } from '../../ui';
import styles from './TimesOfDayList.module.scss';
import { TAG_URL_NAME, TIMES_OF_DAY, useSessionId } from '../../filter';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { TIMES_OF_DAY_KEY } from '../../trips';
import { TOD_URL_NAME } from '../';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useAddTagPopularityMutation } from '../../api';
import { useAppSelector } from '../../../app';

export default function TimesOfDayList() {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const trips = useAppSelector(state => state.filter.trips);
  const isLoadingTrips = useAppSelector(state => state.filter.isLoadingTrips);

  const selectedTag = searchParams.get(TAG_URL_NAME);

  const selectedDateStr = searchParams.get('day');
  const selectedLuxonDate = selectedDateStr
    ? DateTime.fromFormat(selectedDateStr, 'dd.MM.yyyy')
    : undefined;
  const selectedDateTimestamp = selectedLuxonDate?.toMillis();
  const selectedTimesOfDay = searchParams.get(TOD_URL_NAME)?.split(',') ?? [];

  const sesionId = useSessionId();
  const [addTagPopularity] = useAddTagPopularityMutation();
  const [timesOfDayWithAddedPopularity, setTimesOfDayWithAddedPopularity] =
    useState<string[]>([]);

  const now = DateTime.now().setZone(DEFAULT_TIMEZONE);

  const availableTimesOfDay = useMemo(() => {
    if (!trips || isLoadingTrips) {
      return [];
    }

    let availableTimesOfDay: string[] = TIMES_OF_DAY;

    if (selectedLuxonDate?.hasSame(now, 'day')) {
      const currentHour = now.get('hour');

      if (currentHour >= 12 && currentHour < 17) {
        availableTimesOfDay = availableTimesOfDay.filter(
          timeOfDay => timeOfDay !== 'morning'
        );
      } else if (currentHour >= 17 && currentHour < 23) {
        availableTimesOfDay = availableTimesOfDay.filter(
          timeOfDay => timeOfDay !== 'morning' && timeOfDay !== 'day'
        );
      } else if (currentHour >= 23 && currentHour < 6) {
        availableTimesOfDay = availableTimesOfDay.filter(
          timeOfDay =>
            timeOfDay !== 'morning' &&
            timeOfDay !== 'day' &&
            timeOfDay !== 'evening'
        );
      }
    }

    if (
      selectedTag !== null ||
      (selectedDateTimestamp && selectedDateTimestamp > 0)
    ) {
      availableTimesOfDay = availableTimesOfDay.filter(timeOfDay =>
        trips.some((trip: any) => trip[TIMES_OF_DAY_KEY]?.includes(timeOfDay))
      );

      return availableTimesOfDay;
    }

    return availableTimesOfDay;
  }, [selectedTimesOfDay, selectedDateTimestamp, trips]);

  const handleOnChangeTimeOfDay = (timeOfDay: string) => {
    if (!timesOfDayWithAddedPopularity.includes(timeOfDay)) {
      addTagPopularity({ session: sesionId, tag: `TOD${timeOfDay}` });
      setTimesOfDayWithAddedPopularity(prev => [...prev, timeOfDay]);
    }

    const newSearchParams = new URLSearchParams(searchParams);

    const tods = newSearchParams.get(TOD_URL_NAME)?.split(',') ?? [];
    if (tods.includes(timeOfDay)) {
      const filteredTodArr = tods
        .filter(timeOfDayEl => timeOfDayEl !== timeOfDay)
        .join(',');

      if (filteredTodArr.length > 0) {
        newSearchParams.set(TOD_URL_NAME, filteredTodArr);
      } else {
        newSearchParams.delete(TOD_URL_NAME);
      }
    } else {
      tods.push(timeOfDay);
      newSearchParams.set(TOD_URL_NAME, tods.join(','));
    }

    setSearchParams(newSearchParams);
  };

  return (
    <ul className={styles.root}>
      {TIMES_OF_DAY.map(timeOfDay => (
        <li
          className={styles.timeOfDay}
          key={timeOfDay}
        >
          <Checkbox
            checked={selectedTimesOfDay.includes(timeOfDay)}
            disabled={!availableTimesOfDay.includes(timeOfDay)}
            onChange={() => handleOnChangeTimeOfDay(timeOfDay)}
            label={t(timeOfDay)}
            id={timeOfDay}
            variant='checkbox after'
          />
        </li>
      ))}
    </ul>
  );
}
